import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect
} from 'react';
import classNames from 'classnames';
import { Button } from 'antd';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as BackSvg } from '@assets/icons/ShapesDrawingTools/back.svg';
import { ReactComponent as ForwardSvg } from '@assets/icons/ShapesDrawingTools/forward.svg';
import { useOutletContext } from 'react-router-dom';
import {
  getDocumentHighlightViewportPosition,
  getFloatCanvasAnnotations,
  clampFloatCommentPosition
} from '@pages/MediaViewer/components/FloatCommentItem/floatCommentHelpers';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import { AssetItemDto, AssetVersionItemDto } from '@api/Api';
import { createNewComment } from '@redux/actions/mediaViewerAction';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useAuth } from '@hooks/useAuth';
import {
  setAssetListNeedRefresh,
  setListsNeedRefreshInCampaign
} from '@redux/reducers/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useCreateTask } from '@hooks/tasks';
import { useFetch } from '@hooks/useFetch';
import {
  assetStateSelector,
  boardsSelector,
  campaignStateSelector
} from '@redux/selectors/tasks';
import {
  fetchAssetAssignees,
  fetchCampaignTaskBoards
} from '@redux/actions/tasks';
import { FabricContext } from '@context/FabricContext';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import { MediaViewerOutletContextState } from '@layouts/types';
import CommentTask from '@pages/MediaViewer/Comments/components/CommentTask';
import {
  buildCreateCommentPayload,
  createTurnCommentIntoTask,
  mapUppyFilesToAttachments
} from '@pages/MediaViewer/helpers/comment';
import { useCommentMentions } from '@pages/MediaViewer/hooks/useComment';
import getObjectViewportPosition, {
  choosePosition,
  constrainPositionToWindow,
  getObjectViewportRect
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';
import { useResponsive } from '@hooks/useResponsive';
import useCanvasHistory from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useCanvasHistory';
import {
  PIN_DRAW_TOOL,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';
import './FloatCommentEditor.less';
import { IS_UXP_WEBVIEW } from '@config/constants';

const FLOAT_COMMENT_DRAG_THRESHOLD = 5;

function isFloatCommentInteractiveTarget(target: HTMLElement): boolean {
  return !!target.closest(
    'button, a, input, textarea, select, [contenteditable="true"], .ant-dropdown'
  );
}

type FloatCommentEditorProps = {
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  canCreateTask: boolean;
  commentMentions: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
};

function FloatCommentEditor({
  canCreateTask,
  commentMentions,
  asset,
  selectedVersion,
  setSelectedCollapse
}: FloatCommentEditorProps) {
  const isEmbeddedWebView = IS_UXP_WEBVIEW;
  const { isMdWidth, isSmWidth, isLgWidth } = useResponsive();
  const { addNewComment, getPotentialMentions, getPotentialGroupMentions } =
    useOutletContext<MediaViewerOutletContextState>();
  const { clearCanvas, canvas } = useContext(FabricContext);
  const { user } = useAuth();
  const dispatch = useTypedDispatch();
  const createTask = useCreateTask();
  const hasAnnotations = useHasAnnotations();
  const canViewTasks = useAssetMVPermissions('listTasks');
  const { campaignId, workspaceId, id: assetId } = asset;
  const uppy = useMVCommentAttachUppy(assetId);
  const {
    activePageNumber,
    playerTime,
    selectedCommentId,
    canvasMode,
    documentHighlight,
    shapesDrawingTools,
    isMobileExpanded,
    setIsMobileExpanded,
    newCommentTaskDraft,
    setNewCommentIsShowTask,
    setNewCommentSelectedUser,
    setNewCommentSelectedBoard,
    resetNewCommentTaskDraft,
    zoomFactor,
    turnoverDegree,
    activeNewCommentEditor,
    setCanvasMode,
    selectComment,
    setDocumentHighlight,
    setCommentText,
    setIsCommentFieldFocused,
    cancelCommentDraft
  } = useAssetViewerState();
  const shapeRef = useRef(shapesDrawingTools.shape);
  const isShapeDrawing = shapesDrawingTools.shape !== CURSOR_DRAW_TOOL;
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);
  const assetType = selectedVersion?.metadata.type;
  const { history, mods, redoHistory, undoHistory } = useCanvasHistory({
    assetType
  });
  const canUndo = mods < history.length;
  const canRedo = mods > 0;
  const isAudio = assetType === 'video' || assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const checkboxValueType =
    (isDocument && 'pageNumber') || (isAudio && 'timeCode') || null;

  const potentialAssignees = useTypedSelector(
    (state) => assetStateSelector(state, { assetId }).potentialAssignees.list
  );
  const taskBoards = useTypedSelector((state) =>
    boardsSelector(state, { campaignId })
  ).filter((x) => x.id !== 'archived' && x.id !== 'no_assigned');

  useFetch({
    key: `campaign-task-board-${campaignId}`,
    disabled: user?.type !== 'internal' || !campaignId || !canViewTasks,
    selector: (state) =>
      campaignStateSelector(state, { campaignId }).boards.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchCampaignTaskBoards({
          campaignId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setListsNeedRefreshInCampaign({ campaignId }));
  }, [campaignId, dispatch]);

  useFetch({
    key: `asset-assignees-${assetId}`,
    disabled: user?.type !== 'internal' || !assetId || !canViewTasks,
    selector: (state) =>
      assetStateSelector(state, { assetId }).potentialAssignees.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchAssetAssignees({
          workspaceId,
          assetId,
          fetchType
        })
      );
    }
  });

  useEffect(() => {
    dispatch(setAssetListNeedRefresh({ assetId }));
  }, [assetId, dispatch]);

  const onTurnCommentIntoTask = createTurnCommentIntoTask({
    asset,
    user,
    createTask,
    dispatch,
    selectedBoardId: newCommentTaskDraft.selectedBoard?.id,
    selectedUser: newCommentTaskDraft.selectedUser,
    isOpenTaskModal: !isSmWidth,
    setSelectedCollapse,
    openTasksInSidebar: isLgWidth
  });

  const clearCommentDraft = () => {
    clearCanvas();
    setDocumentHighlight(null);
    setCommentText(null);
    resetNewCommentTaskDraft();
    setIsMobileExpanded(false);
    setIsCommentFieldFocused(false);
    isUserPositionedRef.current = false;
    setPosition(null);
  };

  const onCancel = () => {
    clearCommentDraft();
    cancelCommentDraft();
    uppy.cancelAll();
  };

  const onAddComment = async ({ ...values }: SubmitEditorValues) => {
    const data = buildCreateCommentPayload({
      values,
      selectedVersionId: selectedVersion?.id || '',
      isCheckboxChecked,
      isAudioVideo: isAudio,
      isDocument,
      playerTime,
      activePageNumber,
      annotations: getAnnotations()
    });
    const comment = await addNewComment(data, '');
    const files = uppy.getFiles();
    if (files.length) {
      files.forEach((item) => {
        uppy.setFileMeta(item.id, {
          commentId: comment.id,
          readyForUpload: true
        });
      });
      await uppy.upload();
    }
    await createNewComment(
      {
        ...comment,
        attachments: mapUppyFilesToAttachments(files)
      },
      selectedVersion?.id || ''
    );
    if (isLgWidth && !newCommentTaskDraft.isShowTask) {
      setSelectedCollapse?.('comments');
    }
    if (newCommentTaskDraft.isShowTask) {
      onTurnCommentIntoTask(comment);
    }

    const keepToolActive =
      !isMdWidth && shapesDrawingTools.shape !== CURSOR_DRAW_TOOL;
    if (keepToolActive) {
      clearCommentDraft();
      selectComment('new');
      setCanvasMode('edit');
    } else {
      onCancel();
    }

    if (!isMdWidth) {
      const scrollToCreatedComment = () => {
        document
          .querySelector(
            `.media_viewer_comments_container [data-comment-id="${comment.id}"]`
          )
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToCreatedComment);
      });
    }
  };

  const getAnnotations = (): string | null => {
    if (!canvas) return null;
    return getFloatCanvasAnnotations(canvas, {
      requireHasAnnotations: true,
      hasAnnotations
    });
  };

  const { potentialMentions, potentialGroupMentions, setSearchQuery } =
    useCommentMentions({
      assetVersionId: selectedVersion?.id,
      getPotentialMentions,
      getPotentialGroupMentions
    });

  const [hasActiveObject, setHasActiveObject] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isUserPositionedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const isPointerDownRef = useRef(false);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    left: number;
    top: number;
  } | null>(null);

  const updateActiveObjectState = useCallback(() => {
    if (!canvas) return;
    if (canvasMode === 'view') return;
    if (isUserPositionedRef.current) return;
    const drawableObjects = canvas
      .getObjects()
      .filter((obj) => obj.type !== 'highlight' && obj.visible !== false);
    const activeObject = canvas.getActiveObject();
    const anchorObject = activeObject || drawableObjects[0];
    setHasActiveObject(!!anchorObject);
    if (anchorObject) {
      const initialPos = getObjectViewportPosition(canvas, anchorObject);
      const constrained = constrainPositionToWindow(initialPos, { canvas });
      const objectRect = getObjectViewportRect(canvas, anchorObject);
      let pos: { left: number; top: number } | null = constrained;
      if (objectRect && constrained) {
        pos = choosePosition(canvas, objectRect, constrained) ?? constrained;
      }
      setPosition(pos);
    } else {
      setPosition(null);
    }
  }, [canvas, canvasMode]);

  useEffect(() => {
    if (!canvas) return;
    updateActiveObjectState();
    canvas.on('selection:created', updateActiveObjectState);
    canvas.on('selection:updated', updateActiveObjectState);
    canvas.on('selection:cleared', updateActiveObjectState);
    canvas.on('object:modified', updateActiveObjectState);
    canvas.on('object:moving', updateActiveObjectState);
    canvas.on('object:deleted', updateActiveObjectState);
    canvas.on('object:history:add', updateActiveObjectState);
    canvas.on('object:history:remove', updateActiveObjectState);
    canvas.on('object:pin:added', updateActiveObjectState);
    canvas.on('canvas:cleared', updateActiveObjectState);
    return () => {
      canvas.off('selection:created', updateActiveObjectState);
      canvas.off('selection:updated', updateActiveObjectState);
      canvas.off('selection:cleared', updateActiveObjectState);
      canvas.off('object:modified', updateActiveObjectState);
      canvas.off('object:moving', updateActiveObjectState);
      canvas.off('object:deleted', updateActiveObjectState);
      canvas.off('object:history:add', updateActiveObjectState);
      canvas.off('object:history:remove', updateActiveObjectState);
      canvas.off('object:pin:added', updateActiveObjectState);
      canvas.off('canvas:cleared', updateActiveObjectState);
    };
  }, [canvas, updateActiveObjectState]);

  useEffect(() => {
    updateActiveObjectState();
  }, [zoomFactor, turnoverDegree, updateActiveObjectState]);

  const positionFromDocumentHighlight = useMemo(() => {
    if (isMdWidth) return null;
    if (canvasMode === 'view') return null;
    if (!canvas) return null;
    if (!documentHighlight?.rects?.length) return null;
    return getDocumentHighlightViewportPosition(
      canvas,
      documentHighlight.rects
    );
  }, [
    canvas,
    documentHighlight?.text,
    documentHighlight?.rects,
    canvasMode,
    isMdWidth
  ]);

  const isCreatingNewComment = selectedCommentId === 'new';

  const showForActiveObject =
    isCreatingNewComment && hasActiveObject && position;
  const showForDocumentHighlight =
    isCreatingNewComment &&
    !!documentHighlight?.rects?.length &&
    positionFromDocumentHighlight;

  const hasDocumentHighlight = !!documentHighlight?.rects?.length;

  const showForAudioVideoComment =
    isCreatingNewComment &&
    isAudio &&
    shapesDrawingTools.shape === PIN_DRAW_TOOL;

  const shouldShow = isMdWidth
    ? isCreatingNewComment && (isShapeDrawing || hasDocumentHighlight)
    : showForActiveObject ||
      showForDocumentHighlight ||
      showForAudioVideoComment;

  const isEditorFocused =
    !isMdWidth || isMobileExpanded || hasAnnotations || hasDocumentHighlight;

  useEffect(() => {
    if (!isMdWidth || !shouldShow) return;

    const shapeChanged = shapeRef.current !== shapesDrawingTools.shape;
    shapeRef.current = shapesDrawingTools.shape;

    if (shapeChanged && !hasAnnotations && !hasDocumentHighlight) {
      setIsMobileExpanded(false);
    }
  }, [
    isMdWidth,
    shouldShow,
    shapesDrawingTools.shape,
    hasAnnotations,
    hasDocumentHighlight
  ]);

  useEffect(() => {
    if (!shouldShow) return;
    if (!hasAnnotations && !hasDocumentHighlight) return;
    if (isMdWidth) {
      setIsMobileExpanded(true);
    }
  }, [shouldShow, hasAnnotations, hasDocumentHighlight, isMdWidth]);

  // Keep the editor mounted when hidden to avoid unmount side effects
  // that clear attachment state in CommentEditor.
  const clampToViewport = useCallback(
    (pos: { left: number; top: number }) => {
      const el = containerRef.current;
      if (!el) return pos;
      return clampFloatCommentPosition(
        pos,
        {
          width: el.offsetWidth || 0,
          height: el.offsetHeight || 0
        },
        { canvas }
      );
    },
    [canvas]
  );

  const getCurrentPosition = useCallback((): {
    left: number;
    top: number;
  } | null => {
    if (position) return position;
    if (positionFromDocumentHighlight) return positionFromDocumentHighlight;
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { left: rect.left, top: rect.top };
  }, [position, positionFromDocumentHighlight]);

  const onEditorMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMdWidth) return;
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (isFloatCommentInteractiveTarget(target)) return;

      const currentPosition = getCurrentPosition();
      if (!currentPosition) return;

      isPointerDownRef.current = true;
      hasDraggedRef.current = false;
      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        left: currentPosition.left,
        top: currentPosition.top
      };
    },
    [isMdWidth, getCurrentPosition]
  );

  const onEditorClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDraggedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (isMdWidth) return;

    const onMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;

      if (!isDraggingRef.current && isPointerDownRef.current) {
        const deltaX = e.clientX - dragStartRef.current.mouseX;
        const deltaY = e.clientY - dragStartRef.current.mouseY;
        if (Math.hypot(deltaX, deltaY) < FLOAT_COMMENT_DRAG_THRESHOLD) return;

        isDraggingRef.current = true;
        hasDraggedRef.current = true;
        setIsDragging(true);
        isUserPositionedRef.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }

      if (!isDraggingRef.current) return;

      const deltaX = e.clientX - dragStartRef.current.mouseX;
      const deltaY = e.clientY - dragStartRef.current.mouseY;
      setPosition(
        clampToViewport({
          left: dragStartRef.current.left + deltaX,
          top: dragStartRef.current.top + deltaY
        })
      );
    };

    const onMouseUp = () => {
      isPointerDownRef.current = false;
      dragStartRef.current = null;

      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isMdWidth, clampToViewport]);

  useLayoutEffect(() => {
    if (!position || isMdWidth) return;
    const clamped = clampToViewport(position);
    if (clamped.left !== position.left || clamped.top !== position.top) {
      setPosition(clamped);
    }
  }, [position, isMdWidth, clampToViewport]);

  useEffect(() => {
    if (!position || isMdWidth) return;
    const onResize = () => {
      setPosition((prev) => (prev ? clampToViewport(prev) : prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position, isMdWidth, clampToViewport]);

  const isFloatEditorVisible =
    shouldShow && activeNewCommentEditor !== 'sidebar';

  const editorPosition = showForActiveObject
    ? position
    : positionFromDocumentHighlight;

  const usePinToolDefaultPosition =
    showForAudioVideoComment && !isMdWidth && !hasActiveObject;

  const resolvedDesktopPosition =
    position || (!isUserPositionedRef.current ? editorPosition : null);

  const editorStyle = isMdWidth
    ? undefined
    : resolvedDesktopPosition
    ? {
        left: resolvedDesktopPosition.left,
        top: resolvedDesktopPosition.top
      }
    : usePinToolDefaultPosition
    ? {
        left: '50%',
        bottom: 120,
        transform: 'translateX(-50%)'
      }
    : undefined;

  const desktopEditorStyle = editorStyle
    ? {
        ...editorStyle,
        display: isFloatEditorVisible ? undefined : 'none'
      }
    : {
        display: isFloatEditorVisible ? undefined : 'none'
      };

  const showMobileHeader = isMdWidth && !!history?.length;

  return (
    <>
      {showMobileHeader && (
        <div
          className="float_comment_editor_head"
          style={{ display: isFloatEditorVisible ? undefined : 'none' }}
        >
          <div className="float_comment_editor_head-btn">
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              <ArrowLeftSvg />
            </Button>
          </div>
          Add annotation
          <div className="float_comment_editor_head-actions">
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              disabled={!canUndo}
              onClick={(e) => {
                e.stopPropagation();
                undoHistory();
              }}
            >
              <BackSvg
                className={classNames(
                  'float_comment_editor_head-arrow',
                  canUndo && 'float_comment_editor_head-arrow_active'
                )}
              />
            </Button>
            <Button
              htmlType="button"
              className="media_viewer_btn b-only-icon"
              disabled={!canRedo}
              onClick={(e) => {
                e.stopPropagation();
                redoHistory();
              }}
            >
              <ForwardSvg
                className={classNames(
                  'float_comment_editor_head-arrow',
                  canRedo && 'float_comment_editor_head-arrow_active'
                )}
              />
            </Button>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className={classNames('float_comment_editor', {
          is_dragging: isDragging
        })}
        style={
          isMdWidth
            ? { display: isFloatEditorVisible ? undefined : 'none' }
            : desktopEditorStyle
        }
        onMouseDown={!isMdWidth ? onEditorMouseDown : undefined}
        onClickCapture={!isMdWidth ? onEditorClickCapture : undefined}
      >
        <CommentEditor
          commentMentions={commentMentions}
          editCommentId="new"
          setEditCommentId={() => undefined}
          editorFor="new"
          autoFocus={
            hasAnnotations || hasDocumentHighlight || showForAudioVideoComment
          }
          isFocused={isEditorFocused || showForAudioVideoComment}
          replyToCommentId={null}
          setReplyToCommentId={() => undefined}
          onSubmitEditor={onAddComment}
          isCheckboxChecked={isCheckboxChecked}
          setIsCheckboxChecked={setIsCheckboxChecked}
          checkboxValueType={checkboxValueType}
          potentialMentions={potentialMentions}
          potentialGroupMentions={potentialGroupMentions}
          searchPotentialMentions={(v) => setSearchQuery(v)}
          uppy={uppy}
          onRequestExpand={() => setIsMobileExpanded(true)}
        />
        {isEditorFocused && (
          <CommentTask
            selectedUser={newCommentTaskDraft.selectedUser}
            setSelectedUser={setNewCommentSelectedUser}
            selectedBoard={newCommentTaskDraft.selectedBoard}
            setSelectedBoard={setNewCommentSelectedBoard}
            isShowTask={newCommentTaskDraft.isShowTask}
            setIsShowTask={setNewCommentIsShowTask}
            potentialAssignees={potentialAssignees}
            boardList={
              selectedVersion?.versionNumber === asset?.asset?.versionsCount
                ? taskBoards
                : []
            }
            canCreateTask={canCreateTask && user?.type === 'internal'}
            isEmbeddedWebView={isEmbeddedWebView}
            leftSlot={
              <div
                className="float_comment_editor_footer_cancel"
                onClick={onCancel}
              >
                Cancel
              </div>
            }
          />
        )}
      </div>
    </>
  );
}

export default FloatCommentEditor;
